// Почему строка найдена (047: T011; research Р7; spec, сценарий 5; DNS-16 – DNS-18): выделение
// найденного слова и отрывок поля пункта, где оно есть, если его нет в названии.
//
// Правило совпадения — как у поиска сервера (folded в apps/server/src/modules/control/items.ts):
// нижний регистр ru-RU и «ё» как «е». Свёртка — по одному знаку: позиции в свёрнутой строке те же,
// что в исходной, и выделение ложится на исходный текст. Отрывок строится только из полей, которые
// уже приходят в ответе (ControlItem): сервер ищет по ним же — тексту, разделу, номеру,
// ответственному, примечанию и сроку. Нет слова в этих полях — отрывка нет (DNS-18).
//
// Выделение — React-узлы <mark className="kp-hit">, без innerHTML: «<b>&» из данных — текст.
import type { ControlItem } from '@kp/contracts';
import { Fragment, createElement } from 'react';
import type { ReactNode } from 'react';

/** Знаков отрывка с каждой стороны от совпадения — всего около 80 (DNS-16). */
const SNIPPET_SIDE = 40;

/** Один знак в свёртке поиска: нижний регистр ru-RU, «ё» → «е»; длина знака не меняется. */
export function foldChar(char: string): string {
  const lower = char.toLocaleLowerCase('ru-RU');
  if (lower.length !== char.length) return char;
  return lower === 'ё' ? 'е' : lower;
}

function fold(text: string): string {
  let result = '';
  for (let index = 0; index < text.length; index++) result += foldChar(text[index]!);
  return result;
}

/** Все вхождения поиска в тексте: [начало, конец) по исходной строке, без перекрытий. */
export function matchRanges(text: string, q: string): [number, number][] {
  const needle = fold(q.trim());
  if (!needle) return [];
  const haystack = fold(text);
  const ranges: [number, number][] = [];
  for (let at = haystack.indexOf(needle); at >= 0; at = haystack.indexOf(needle, at)) {
    ranges.push([at, at + needle.length]);
    at += needle.length;
  }
  return ranges;
}

export interface ItemSnippet {
  /** Подпись поля: «Примечание», «Ответственный», «Срок», «Раздел», «Номер». */
  label: string;
  /** Около 80 знаков вокруг первого совпадения; обрезанный край — «…». */
  text: string;
}

type SnippetFields = Pick<
  ControlItem,
  'text' | 'comment' | 'responsible' | 'dueRaw' | 'section' | 'number'
>;

/**
 * Отрывок поля, где найдено слово, если его нет в названии (DNS-16): первое поле с совпадением
 * по порядку — примечание, ответственный, срок, раздел, номер. Края — по пробелу, пробелы и
 * переносы строк — одним пробелом.
 */
export function itemSnippet(item: SnippetFields, q: string): ItemSnippet | null {
  if (matchRanges(item.text, q).length > 0) return null;
  const fields: [string, string | null | undefined][] = [
    ['Примечание', item.comment],
    ['Ответственный', item.responsible],
    ['Срок', item.dueRaw],
    ['Раздел', item.section],
    ['Номер', item.number],
  ];
  for (const [label, raw] of fields) {
    const value = raw?.replace(/\s+/g, ' ').trim();
    if (!value) continue;
    const first = matchRanges(value, q)[0];
    if (!first) continue;
    const [at, end] = first;
    let from = Math.max(0, at - SNIPPET_SIDE);
    let to = Math.min(value.length, end + SNIPPET_SIDE);
    if (from > 0) {
      const space = value.indexOf(' ', from);
      if (space >= 0 && space < at) from = space + 1;
    }
    if (to < value.length) {
      const space = value.lastIndexOf(' ', to);
      if (space >= end) to = space;
    }
    const text = `${from > 0 ? '…' : ''}${value.slice(from, to).trim()}${to < value.length ? '…' : ''}`;
    return { label, text };
  }
  return null;
}

/** Текст с выделенными вхождениями поиска (DNS-17); без совпадений — текст как есть. */
export function Highlight({ text, q }: { text: string; q: string }): ReactNode {
  const ranges = matchRanges(text, q);
  if (ranges.length === 0) return text;
  const nodes: ReactNode[] = [];
  let from = 0;
  for (const [at, end] of ranges) {
    if (at > from) nodes.push(text.slice(from, at));
    nodes.push(createElement('mark', { key: at, className: 'kp-hit' }, text.slice(at, end)));
    from = end;
  }
  if (from < text.length) nodes.push(text.slice(from));
  return createElement(Fragment, null, ...nodes);
}
